import { IComplexKeyValPair } from 'app/interfaces/IComplexKeyValPair';
import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { format } from 'app/shared/utils/string.util';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { BaseSchemaModifierTransformation } from './base-schema-modifier-transformation';
import { ColumnExpressionProcessor } from './column-expression-processor';
import { DocumentForm } from './dataflow-source';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { StringDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';

export enum ParseFormat {
    json = 'json',
    delimitedText = 'delimited',
    xml = 'xml'
}

export interface IJsonFormatSettings {
    documentForm: DataFlowExpression;
}

export interface IDelimitedTextFormatSettings {
    columnNamesAsHeader: boolean;
    columnDelimiter: string;
    nullValue: string;
}

export interface IXmlFormatSettings {
    namespaces: boolean;
    namespacePrefixes: Array<IComplexKeyValPair<string, string>>;
    nullValue: string;
}
@Metamodel.model({
    name: 'parse',
    display: [stringResources.DataFlowParseTransformationLabel, stringResources.DataFlowParseTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    }
})
export class ParseTransformation extends BaseSchemaModifierTransformation {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        constraint: {
            required: true,
            validator: ParseTransformation.validateParseFormat
        }
    })
    public format: ParseFormat = ParseFormat.json;
    public jsonSettings: IJsonFormatSettings = {
        documentForm: this.createTemplatableExpression(StringDataFlowType.TYPE, DocumentForm.documentPerLine)
    };
    public delimitedTextSettings: IDelimitedTextFormatSettings = {
        columnNamesAsHeader: false,
        columnDelimiter: ',',
        nullValue: ''
    };
    public xmlSettings: IXmlFormatSettings = {
        namespaces: true,
        namespacePrefixes: [],
        nullValue: ''
    };

    public constructor() {
        super();
        this.inspectSummaryOutputFields.splice(1, 0, {
            name: () => stringResources.TransformationInspect_Update,
            icon: () => 'svg-i-9x9-Update',
            value: () => this.overwrittenColumns.length
        });
        this.inspectDetailsOutputFields.unshift({
            name: () => this.columnInfoTitle,
            icon: this._getUpdatedIcon.bind(this)
        });
        this.className = 'Parse';
    }

    public get columns(): DataFlowColumn[] {
        const derivedColumns = ColumnExpressionProcessor.processNewColumns(this.expressionColumns, this.distinctInputColumnRefs, this).map((processedColumnResult) => processedColumnResult.column);
        const columns: DataFlowColumn[] = [];
        this.overwrittenColumns = [];
        this.newColumns = [];
        this.unchangedColumns = [];
        for (const column of this.inputColumns) {
            const overwrittenColumnIndex = derivedColumns.findIndex((derivedColumn) => (derivedColumn.name) === column.name);
            if (overwrittenColumnIndex > -1) {
                const overwrittenColumn = derivedColumns[overwrittenColumnIndex];
                if (columns.indexOf(overwrittenColumn) < 0) {
                    columns.push(overwrittenColumn);
                }
                if (this.overwrittenColumns.indexOf(overwrittenColumn) < 0) {
                    this.overwrittenColumns.push(overwrittenColumn);
                }
            } else {
                this.unchangedColumns.push(column);
                columns.push(column);
            }
        }

        for (const column of derivedColumns) {
            if (columns.indexOf(column) < 0) {
                columns.push(column);
                if (this.overwrittenColumns.indexOf(column) < 0) {
                    this.newColumns.push(column);
                }
            }
        }
        return columns;
    }

    public static validateParseFormat(transformation: ParseTransformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.format === ParseFormat.delimitedText) {
            if (!transformation.delimitedTextSettings.columnDelimiter) {
                violations.push({
                    context: transformation,
                    message: () => stringResources.DataFlowParseWithNoDelimiterError
                });
            }
        }

        return violations;
    }

    protected getGeneratedDescription(): string {
        if (!!this.outputCacheId) {
            const columnNames = this.outputColumns().map((column) => column.name).filter((columnName) => !!columnName);
            if (columnNames) {
                return format(stringResources.DeriveDescription, columnNames.join(', '));
            }
        }
        return stringResources.ParseTransformationDescription;
    }
}
